import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

const CONTACT_IMG =
    "https://images.unsplash.com/photo-1581356465504-d140ac8c68a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxwbHVtYmVyJTIwd29ya2luZyUyMGNvbnN0cnVjdGlvbnxlbnwwfHx8fDE3Nzc5MzA4OTh8MA&ixlib=rb-4.1.0&q=85";

export default function Contact() {
    useDocumentMeta("contact", "/contact");
    const { t } = useI18n();
    const c = t.contact;

    return (
        <div data-testid="contact-page" className="bg-white">
            <section className="bg-zinc-100 border-b-2 border-black">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
                    <div className="overline mb-5">{c.headerOverline}</div>
                    <h1 className="font-display text-6xl md:text-7xl lg:text-8xl uppercase leading-[0.85] text-black">
                        {c.headerTitle1}
                        <br />
                        <span className="text-[#FF5A00]">{c.headerTitle2}</span>
                    </h1>
                    <p className="mt-6 text-lg md:text-xl font-medium text-zinc-700 max-w-2xl">
                        {c.headerDesc}
                    </p>
                </div>
            </section>

            <section className="border-b-2 border-black">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14">
                        <div className="lg:col-span-3 border-2 border-black bg-white p-8 md:p-10 shadow-[12px_12px_0px_#111111]">
                            <div className="overline mb-2">{c.sideOverline}</div>
                            <h2 className="font-display text-4xl md:text-5xl uppercase leading-none text-black">
                                {c.contactTitle || c.formTitle}
                            </h2>
                            <p className="mt-6 text-zinc-700 text-base leading-relaxed max-w-2xl">
                                {c.contactDescription || c.headerDesc}
                            </p>
                        </div>

                        <aside className="lg:col-span-2 space-y-6">
                            <div className="border-2 border-black bg-black text-white p-8">
                                <div className="overline text-[#FF5A00] mb-4">{c.sideOverline}</div>
                                <ul className="space-y-5">
                                    <li className="flex items-start gap-4">
                                        <div className="w-10 h-10 border-2 border-white flex items-center justify-center shrink-0">
                                            <Phone className="w-4 h-4 text-[#FF5A00]" />
                                        </div>
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-zinc-400">
                                                {c.sideCallLabel}
                                            </div>
                                            <a
                                                href="tel:+15818889419"
                                                className="font-display text-2xl tracking-wide hover:text-[#FF5A00]"
                                                data-testid="contact-phone"
                                            >
                                                {t.common.phoneShort}
                                            </a>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-4">
                                        <div className="w-10 h-10 border-2 border-white flex items-center justify-center shrink-0">
                                            <Mail className="w-4 h-4 text-[#FF5A00]" />
                                        </div>
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-zinc-400">
                                                {c.sideEmailLabel}
                                            </div>
                                            <a
                                                href="mailto:hello@plombtechreno.com"
                                                className="font-bold hover:text-[#FF5A00] break-all"
                                                data-testid="contact-email"
                                            >
                                                {t.common.email}
                                            </a>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-4">
                                        <div className="w-10 h-10 border-2 border-white flex items-center justify-center shrink-0">
                                            <MapPin className="w-4 h-4 text-[#FF5A00]" />
                                        </div>
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-zinc-400">
                                                {c.sideAreaLabel}
                                            </div>
                                            <div className="font-bold">{c.sideAreaValue}</div>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-4">
                                        <div className="w-10 h-10 border-2 border-white flex items-center justify-center shrink-0">
                                            <Clock className="w-4 h-4 text-[#FF5A00]" />
                                        </div>
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-zinc-400">
                                                {c.sideHoursLabel}
                                            </div>
                                            <div className="font-bold">{c.sideHoursValue}</div>
                                            <div className="text-sm text-zinc-300">{c.sideHoursNote}</div>
                                        </div>
                                    </li>
                                </ul>
                            </div>

                            <div className="relative">
                                <img src={CONTACT_IMG} alt="Plumber on call" className="w-full h-[260px] object-cover border-2 border-black grayscale" />
                                <div className="absolute top-4 left-4 bg-[#FF5A00] text-black px-4 py-2 border-2 border-black text-xs uppercase tracking-[0.3em] font-bold">
                                    {c.sideImageTag}
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </section>
        </div>
    );
}
